/**
 * .mobileconfig parser for PPPC TCC payloads.
 *
 * A .mobileconfig is an Apple property-list (plist) XML document containing one
 * or more "PayloadContent" entries. We extract every entry whose
 * PayloadType is `com.apple.TCC.configuration-profile-policy`, then walk its
 * `Services` dict to recover each (service key → entries) mapping.
 *
 * The parser is deliberately defensive: .mobileconfig files come from many
 * sources (Jamf, Kandji, MDMs, hand-rolled), and we want a clear error rather
 * than a partial import when the structure is unexpected.
 */
import { PPPC_TCC_IDENTIFIER, PppcChoicesMap, PppcIdentifierType } from "@/types/pppc";

/** A single (Identifier + CodeRequirement) combo found in the file. */
export interface ParsedPppcApp {
  /** Stable id for UI list keys. */
  id: string;
  identifier: string;
  identifierType: PppcIdentifierType;
  codeRequirement: string;
  /** Allow/Deny choices keyed by TCC service name. */
  choices: PppcChoicesMap;
  /** Display friendly count. */
  serviceCount: number;
  /** Optional payload display name for the source app group. */
  sourceLabel?: string;
}

export interface ParsedMobileconfig {
  apps: ParsedPppcApp[];
  /** Top-level profile display name (PayloadDisplayName), if present. */
  profileName?: string;
  /** Non-fatal warnings collected while parsing. */
  warnings: string[];
}

export class MobileconfigParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MobileconfigParseError";
  }
}

/** Browser-safe DOMParser access. Throws a clear error in non-DOM environments. */
const getDomParser = (): DOMParser => {
  if (typeof DOMParser === "undefined") {
    throw new MobileconfigParseError(
      "Mobileconfig parsing requires a browser environment (DOMParser unavailable).",
    );
  }
  return new DOMParser();
};

/**
 * Convert a plist XML node into a JS value. Handles dict, array, string,
 * integer, real, true, false, data, date.
 */
const plistToJs = (node: Element): unknown => {
  switch (node.nodeName.toLowerCase()) {
    case "dict": {
      const out: Record<string, unknown> = {};
      const children = Array.from(node.children);
      for (let i = 0; i < children.length; i++) {
        const k = children[i];
        if (k.nodeName.toLowerCase() !== "key") continue;
        const valueNode = children[i + 1];
        if (!valueNode) continue;
        out[k.textContent ?? ""] = plistToJs(valueNode);
        i++;
      }
      return out;
    }
    case "array":
      return Array.from(node.children).map(plistToJs);
    case "string":
      return node.textContent ?? "";
    case "integer":
      return Number(node.textContent ?? "0");
    case "real":
      return Number(node.textContent ?? "0");
    case "true":
      return true;
    case "false":
      return false;
    case "data":
      return (node.textContent ?? "").trim();
    case "date":
      return node.textContent ?? "";
    default:
      return node.textContent ?? "";
  }
};

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Pluck the first child element regardless of whitespace text nodes. */
const firstElementChild = (parent: Element): Element | null => {
  for (const c of Array.from(parent.children)) {
    if (c.nodeType === 1) return c;
  }
  return null;
};

/**
 * Parse a .mobileconfig file's text content and return every TCC entry it
 * carries, grouped by (Identifier, CodeRequirement) so the UI can present a
 * clean picker.
 */
export const parseMobileconfig = (xmlText: string): ParsedMobileconfig => {
  if (!xmlText || xmlText.trim().length === 0) {
    throw new MobileconfigParseError("File is empty.");
  }

  // Reject files that are clearly signed (CMS / PKCS#7). Apple-signed profiles
  // start with binary CMS data and cannot be parsed without first stripping
  // the signature — out of scope for the browser.
  if (!xmlText.trimStart().startsWith("<?xml") && !xmlText.includes("<plist")) {
    throw new MobileconfigParseError(
      "This file appears to be a signed .mobileconfig (PKCS#7). Please export the unsigned XML version and try again.",
    );
  }

  const parser = getDomParser();
  const doc = parser.parseFromString(xmlText, "application/xml");
  const parserError = doc.querySelector("parsererror");
  if (parserError) {
    throw new MobileconfigParseError("File is not valid XML.");
  }

  const plist = doc.querySelector("plist");
  if (!plist) {
    throw new MobileconfigParseError("Not a plist file (missing <plist> root).");
  }
  const root = firstElementChild(plist);
  if (!root) {
    throw new MobileconfigParseError("Plist is empty.");
  }
  const rootValue = plistToJs(root);
  if (!isObject(rootValue)) {
    throw new MobileconfigParseError("Plist root must be a dictionary.");
  }

  const profileName =
    typeof rootValue["PayloadDisplayName"] === "string"
      ? (rootValue["PayloadDisplayName"] as string)
      : undefined;

  const payloadContent = rootValue["PayloadContent"];
  if (!Array.isArray(payloadContent)) {
    throw new MobileconfigParseError(
      "PayloadContent missing or not an array — nothing to import.",
    );
  }

  const warnings: string[] = [];
  // Dedupe key -> ParsedPppcApp so multiple TCC payloads referencing the same
  // (identifier, codereq, idType) merge into one row in the picker.
  const buckets = new Map<string, ParsedPppcApp>();

  for (const payload of payloadContent) {
    if (!isObject(payload)) continue;
    if (payload["PayloadType"] !== PPPC_TCC_IDENTIFIER) continue;

    const sourceLabel =
      typeof payload["PayloadDisplayName"] === "string"
        ? (payload["PayloadDisplayName"] as string)
        : undefined;

    const services = payload["Services"];
    if (!isObject(services)) {
      warnings.push("A TCC payload was skipped because it had no Services dict.");
      continue;
    }

    for (const [serviceKey, raw] of Object.entries(services)) {
      const entries = Array.isArray(raw) ? raw : [];
      for (const entry of entries) {
        if (!isObject(entry)) continue;
        const identifier = entry["Identifier"];
        const idTypeRaw = entry["IdentifierType"];
        const codeReq = entry["CodeRequirement"];
        const allowed = entry["Allowed"];

        if (typeof identifier !== "string" || identifier.length === 0) {
          warnings.push(`Skipped a ${serviceKey} entry with no Identifier.`);
          continue;
        }
        const identifierType: PppcIdentifierType =
          idTypeRaw === "path" ? "path" : "bundleID";
        const codeRequirement = typeof codeReq === "string" ? codeReq : "";

        // Apple TCC supports Allowed:bool (legacy) and AuthorizationValue:int
        // (newer, allow=2/deny=0). Fall back to Allowed if AuthorizationValue
        // is absent.
        let choice: "allow" | "deny" | undefined;
        const auth = entry["AuthorizationValue"];
        if (typeof auth === "number") {
          choice = auth >= 2 ? "allow" : "deny";
        } else if (typeof allowed === "boolean") {
          choice = allowed ? "allow" : "deny";
        } else {
          warnings.push(
            `Skipped ${serviceKey} for ${identifier}: missing Allowed/AuthorizationValue.`,
          );
          continue;
        }

        const bucketKey = `${identifierType}::${identifier}::${codeRequirement}`;
        let bucket = buckets.get(bucketKey);
        if (!bucket) {
          bucket = {
            id: bucketKey,
            identifier,
            identifierType,
            codeRequirement,
            choices: {},
            serviceCount: 0,
            sourceLabel,
          };
          buckets.set(bucketKey, bucket);
        }

        // If the same service appears more than once for the same app, keep
        // the first decision and warn on conflicts.
        const existing = bucket.choices[serviceKey];
        if (existing && existing !== choice) {
          warnings.push(
            `Conflicting decisions for ${serviceKey} on ${identifier} — keeping "${existing}".`,
          );
          continue;
        }
        if (!existing) {
          bucket.choices[serviceKey] = choice;
          bucket.serviceCount += 1;
        }
      }
    }
  }

  const apps = Array.from(buckets.values()).filter((a) => a.serviceCount > 0);
  if (apps.length === 0) {
    throw new MobileconfigParseError(
      "No PPPC (TCC) payloads with privacy services were found in this file.",
    );
  }

  return { apps, profileName, warnings };
};
